import { supabase } from '../lib/supabase';

export const vehicleService = {
  // VIN validation and processing
  validateVIN(vin) {
    // Basic VIN validation - 17 characters, alphanumeric except I, O, Q
    const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
    return vinRegex?.test(vin?.toString()?.toUpperCase());
  },

  // Process VIN and extract vehicle information
  async processVIN(vin) {
    try {
      if (!this.validateVIN(vin)) {
        throw new Error('Invalid VIN format');
      }

      const cleanVIN = vin?.toUpperCase();

      // Basic VIN decoding (position-based information)
      const vehicleInfo = {
        vin: cleanVIN,
        // Position 1-3: World Manufacturer Identifier (WMI)
        manufacturer_code: cleanVIN?.substring(0, 3),
        // Position 4-8: Vehicle Descriptor Section (VDS)
        vehicle_descriptor: cleanVIN?.substring(3, 8),
        // Position 10: Model Year
        year: this.decodeModelYear(cleanVIN?.charAt(9)),
        // Position 11: Plant Code
        plant_code: cleanVIN?.charAt(10),
        // Position 12-17: Sequential Number
        serial_number: cleanVIN?.substring(11, 17)
      };

      // Try to determine make from WMI
      vehicleInfo.make = this.decodeMakeFromWMI(vehicleInfo?.manufacturer_code);

      return { data: vehicleInfo, error: null };
    } catch (error) {
      console.error('Error processing VIN:', error);
      return { data: null, error };
    }
  },

  // Decode model year from VIN position 10
  decodeModelYear(yearCode) {
    const yearMap = {
      'A': 1980, 'B': 1981, 'C': 1982, 'D': 1983, 'E': 1984, 'F': 1985, 'G': 1986, 'H': 1987,
      'J': 1988, 'K': 1989, 'L': 1990, 'M': 1991, 'N': 1992, 'P': 1993, 'R': 1994, 'S': 1995,
      'T': 1996, 'V': 1997, 'W': 1998, 'X': 1999, 'Y': 2000, '1': 2001, '2': 2002, '3': 2003,
      '4': 2004, '5': 2005, '6': 2006, '7': 2007, '8': 2008, '9': 2009, 'A': 2010, 'B': 2011,
      'C': 2012, 'D': 2013, 'E': 2014, 'F': 2015, 'G': 2016, 'H': 2017, 'J': 2018, 'K': 2019,
      'L': 2020, 'M': 2021, 'N': 2022, 'P': 2023, 'R': 2024, 'S': 2025
    };

    return yearMap?.[yearCode] || new Date()?.getFullYear();
  },

  // Decode manufacturer from WMI
  decodeMakeFromWMI(wmi) {
    const makeMap = {
      '1HG': 'Honda', '1HT': 'Honda', '19X': 'Honda',
      '1FA': 'Ford', '1FT': 'Ford', '1FM': 'Ford',
      '1G1': 'Chevrolet', '1GC': 'Chevrolet', '1GT': 'Chevrolet',
      '1N4': 'Nissan', '1N6': 'Nissan',
      '4T1': 'Toyota', '4T3': 'Toyota', '5NP': 'Toyota',
      '2HG': 'Honda', '2HT': 'Honda',
      '3VW': 'Volkswagen', '1VW': 'Volkswagen',
      'WBA': 'BMW', 'WBS': 'BMW', 'WBY': 'BMW',
      'WDD': 'Mercedes-Benz', 'WDC': 'Mercedes-Benz',
      'WAU': 'Audi', 'WA1': 'Audi'
    };

    return makeMap?.[wmi] || 'Unknown';
  },

  // Get all vehicles with optional filters
  async getAllVehicles(filters = {}) {
    try {
      let query = supabase?.from('vehicles')?.select(`
          *,
          car_house:car_houses(id, name, code)
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.car_house_id) {
        query = query?.eq('car_house_id', filters?.car_house_id);
      }
      if (filters?.make) {
        query = query?.eq('make', filters?.make);
      }
      if (filters?.model) {
        query = query?.ilike('model', `%${filters?.model}%`);
      }
      if (filters?.year) {
        query = query?.eq('year', filters?.year);
      }
      if (filters?.search) {
        query = query?.or(`vin.ilike.%${filters?.search}%,make.ilike.%${filters?.search}%,model.ilike.%${filters?.search}%,owner_name.ilike.%${filters?.search}%,license_plate.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return { data: null, error };
    }
  },

  // Get vehicle by VIN
  async getVehicleByVIN(vin) {
    try {
      if (!this.validateVIN(vin)) {
        throw new Error('Invalid VIN format');
      }

      const { data, error } = await supabase?.from('vehicles')?.select(`
          *,
          car_house:car_houses(*),
          orders(
            id, order_number, status, title,
            created_at, total_amount
          )
        `)?.eq('vin', vin?.toUpperCase())?.single();

      if (error && error?.code !== 'PGRST116') throw error; // PGRST116 is "not found"

      return { data, error: error?.code === 'PGRST116' ? null : error };
    } catch (error) {
      console.error('Error fetching vehicle by VIN:', error);
      return { data: null, error };
    }
  },

  // Get vehicle by ID
  async getVehicleById(id) {
    try {
      const { data, error } = await supabase?.from('vehicles')?.select(`
          *,
          car_house:car_houses(*),
          orders(
            id, order_number, status, title, priority,
            created_at, total_amount, customer_name,
            assigned_technician:technicians!assigned_technician_id(first_name, last_name)
          )
        `)?.eq('id', id)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      return { data: null, error };
    }
  },

  // Create new vehicle
  async createVehicle(vehicleData) {
    try {
      // Validate VIN if provided
      if (vehicleData?.vin && !this.validateVIN(vehicleData?.vin)) {
        throw new Error('Invalid VIN format');
      }

      // Process and clean data
      const cleanData = {
        ...vehicleData,
        vin: vehicleData?.vin?.toUpperCase(),
        make: vehicleData?.make?.trim(),
        model: vehicleData?.model?.trim(),
        year: parseInt(vehicleData?.year),
        mileage: parseInt(vehicleData?.mileage) || 0,
        created_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('vehicles')?.insert([cleanData])?.select(`
          *,
          car_house:car_houses(*)
        `)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error creating vehicle:', error);
      return { data: null, error };
    }
  },

  // Update vehicle
  async updateVehicle(id, updates) {
    try {
      // Validate VIN if being updated
      if (updates?.vin && !this.validateVIN(updates?.vin)) {
        throw new Error('Invalid VIN format');
      }

      // Process updates
      const cleanUpdates = {
        ...updates,
        ...(updates?.vin && { vin: updates?.vin?.toUpperCase() }),
        ...(updates?.year && { year: parseInt(updates?.year) }),
        ...(updates?.mileage && { mileage: parseInt(updates?.mileage) }),
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('vehicles')?.update(cleanUpdates)?.eq('id', id)?.select(`
          *,
          car_house:car_houses(*)
        `)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating vehicle:', error);
      return { data: null, error };
    }
  },

  // Delete vehicle
  async deleteVehicle(id) {
    try {
      // Check if vehicle has orders
      const { data: orders, error: ordersError } = await supabase?.from('orders')?.select('id')?.eq('vehicle_id', id)?.limit(1);

      if (ordersError) throw ordersError;

      if (orders && orders?.length > 0) {
        throw new Error('Cannot delete vehicle with existing orders');
      }

      const { error } = await supabase?.from('vehicles')?.delete()?.eq('id', id);

      if (error) throw error;

      return { data: true, error: null };
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      return { data: null, error };
    }
  },

  // Get vehicle service history
  async getVehicleServiceHistory(vehicleId) {
    try {
      const { data, error } = await supabase?.from('orders')?.select(`
          id, order_number, status, title, description,
          created_at, actual_completion, total_amount,
          assigned_technician:technicians!assigned_technician_id(first_name, last_name),
          order_service_items(
            *,
            service_item:service_items(name, service_type_id)
          )
        `)?.eq('vehicle_id', vehicleId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching vehicle service history:', error);
      return { data: null, error };
    }
  },

  // Get vehicle makes (unique values from database)
  async getVehicleMakes() {
    try {
      const { data, error } = await supabase?.from('vehicles')?.select('make')?.not('make', 'is', null);

      if (error) throw error;

      // Get unique makes
      const uniqueMakes = [...new Set(data?.map(v => v.make))]?.sort();

      return { data: uniqueMakes, error: null };
    } catch (error) {
      console.error('Error fetching vehicle makes:', error);
      return { data: [], error };
    }
  },

  // Get vehicle models for a specific make
  async getVehicleModels(make) {
    try {
      const { data, error } = await supabase?.from('vehicles')?.select('model')?.eq('make', make)?.not('model', 'is', null);

      if (error) throw error;

      // Get unique models
      const uniqueModels = [...new Set(data?.map(v => v.model))]?.sort();

      return { data: uniqueModels, error: null };
    } catch (error) {
      console.error('Error fetching vehicle models:', error);
      return { data: [], error };
    }
  },

  // Get vehicle years (unique values)
  async getVehicleYears() {
    try {
      const { data, error } = await supabase?.from('vehicles')?.select('year')?.not('year', 'is', null)?.order('year', { ascending: false });

      if (error) throw error;

      // Get unique years
      const uniqueYears = [...new Set(data?.map(v => v.year))];

      return { data: uniqueYears, error: null };
    } catch (error) {
      console.error('Error fetching vehicle years:', error);
      return { data: [], error };
    }
  },

  // Scan VIN using Edge Function (placeholder for actual implementation)
  async scanVINFromImage(imageFile) {
    try {
      // This would integrate with an Edge Function for VIN scanning
      const formData = new FormData();
      formData?.append('image', imageFile);

      // For now, return a mock response
      // In production, this would call a Supabase Edge Function
      const mockVIN = '1HGCM82633A123456'; // Mock VIN for development
      
      return await this.processVIN(mockVIN);
    } catch (error) {
      console.error('Error scanning VIN from image:', error);
      return { data: null, error };
    }
  },

  // Bulk import vehicles from CSV
  async bulkImportVehicles(vehiclesData) {
    try {
      // Validate and clean data
      const cleanedData = vehiclesData?.map(vehicle => ({
        ...vehicle,
        vin: vehicle?.vin?.toUpperCase(),
        year: parseInt(vehicle?.year),
        mileage: parseInt(vehicle?.mileage) || 0,
        created_at: new Date()?.toISOString()
      }))?.filter(vehicle => this.validateVIN(vehicle?.vin));

      if (cleanedData?.length === 0) {
        throw new Error('No valid vehicles to import');
      }

      const { data, error } = await supabase?.from('vehicles')?.insert(cleanedData)?.select();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error bulk importing vehicles:', error);
      return { data: null, error };
    }
  }
};

export default vehicleService;